import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import VersionWatcher from "@/components/VersionWatcher";
import PostLoginGate from "@/components/PostLoginGate";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Marketplace from "@/pages/Marketplace";
import ProductDetail from "@/pages/ProductDetail";
import Designers from "@/pages/Designers";
import DesignerProfile from "@/pages/DesignerProfile";
import Auth from "@/pages/Auth";
import PaymentResult from "@/pages/PaymentResult";
import StaticPage from "@/pages/StaticPage";
import FaqPage from "@/pages/FaqPage";
import VisualSearch from "@/pages/VisualSearch";
import Cart from "@/pages/Cart";
import VendorApply from "@/pages/VendorApply";
import VendorLogin from "@/pages/VendorLogin";
import VendorDashboard from "@/pages/VendorDashboard";
import TermsPage from "@/pages/TermsPage";
import Grievance from "@/pages/Grievance";
import TermsGate from "@/components/TermsGate";
import OnboardingTour from "@/components/OnboardingTour";
import { CartProvider } from "@/context/CartContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import BuyerDashboard from "@/pages/BuyerDashboard";
import SellerDashboard from "@/pages/SellerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MobileTabBar from "@/components/MobileTabBar";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function PublicShell({ children }) {
  return (
    <>
      <Navbar />
      {/* pb-16 on mobile keeps the fixed bottom tab bar off the content */}
      <main className="page pb-16 lg:pb-0">{children}</main>
      <Footer />
      <MobileTabBar />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CurrencyProvider>
          <CartProvider>
          <Routes>
            <Route path="/" element={<PublicShell><Home /></PublicShell>} />
            <Route path="/marketplace" element={<PublicShell><Marketplace /></PublicShell>} />
            <Route path="/visual-search" element={<PublicShell><VisualSearch /></PublicShell>} />
            <Route path="/cart" element={<PublicShell><Cart /></PublicShell>} />
            <Route path="/design/:id" element={<PublicShell><ProductDetail /></PublicShell>} />
            <Route path="/designers" element={<PublicShell><Designers /></PublicShell>} />
            <Route path="/designer/:id" element={<PublicShell><DesignerProfile /></PublicShell>} />
            <Route path="/login" element={<Auth mode="login" />} />
            <Route path="/register" element={<Auth mode="register" />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/payment/success" element={<PublicShell><PaymentResult /></PublicShell>} />
            <Route path="/payment/cancel" element={<PublicShell><PaymentResult cancel /></PublicShell>} />
            <Route path="/vendor/apply" element={<PublicShell><VendorApply /></PublicShell>} />
            <Route path="/vendor/login" element={<PublicShell><VendorLogin /></PublicShell>} />
            <Route path="/vendor" element={<Protected roles={["cam_vendor", "admin"]}><VendorDashboard /></Protected>} />
            <Route path="/terms" element={<PublicShell><TermsPage fixedRole="buyer" /></PublicShell>} />
            <Route path="/terms/:slug" element={<PublicShell><TermsPage /></PublicShell>} />
            <Route path="/privacy" element={<PublicShell><TermsPage fixedRole="privacy" /></PublicShell>} />
            <Route path="/grievance" element={<PublicShell><Grievance /></PublicShell>} />
            <Route path="/faq" element={<PublicShell><FaqPage /></PublicShell>} />
            {["about", "how-it-works", "pricing", "refund-policy", "contact"].map((p) => (
              <Route key={p} path={`/${p}`} element={<PublicShell><StaticPage slug={p} /></PublicShell>} />
            ))}
            <Route path="/buyer" element={<Protected roles={["buyer", "both"]}><BuyerDashboard /></Protected>} />
            <Route path="/seller" element={<Protected roles={["seller", "both"]}><SellerDashboard /></Protected>} />
            <Route path="/admin" element={<Protected roles={["admin", "employee"]}><AdminDashboard /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <TermsGate />
          <OnboardingTour />
          <PostLoginGate />
          </CartProvider>
          </CurrencyProvider>
        </AuthProvider>
        <Toaster />
        <VersionWatcher />
      </BrowserRouter>
    </div>
  );
}

export default App;
